import React, { useState, useRef, useEffect } from 'react'
import { Table } from 'react-bootstrap'
import Header from '../header/header'
import Navvbar from '../header/Navvbar'
import { IoCaretDown } from "react-icons/io5";
import { FaRedo } from "react-icons/fa";
import OutsourcePayment from "../outsourcefinances/OutsourcePayment"
import OutsourceCredit from './OutsourceCredit';
import { useNavigate } from 'react-router-dom';
import OutsourceFilterModel from './outsourceFilterModel';
import FinanceSidebar from '../dashboard/finances/Financesidebar';
import { useCurrency } from '../../context/currencyContext';

const API_BASE_URL = 'https://hms.backend.hiragroupcloud.com';

// ✅ helper to read auth token from localStorage
const getAuthHeaders = () => {
  const token =
    localStorage.getItem('accessToken') ||
    localStorage.getItem('authToken') ||
    localStorage.getItem('token') ||
    localStorage.getItem('jwt') ||
    localStorage.getItem('idToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const OutsourcePosting = () => {
  const [showDropdown, setShowDropdown] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedType, setSelectedType] = useState('')
  const [showCreditModal, setShowCreditModal] = useState(false)
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false)
  const [entries, setEntries] = useState([])
  const [filteredEntries, setFilteredEntries] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [hoveredRowId, setHoveredRowId] = useState(null)

  const [sourceLabs, setSourceLabs] = useState([])

  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    type: 'All',
    account: 'All',
    accounts: ['All'],
    format: 'On Screen'
  })
  const dropdownRef = useRef(null)
  const navigate = useNavigate();

  // ✅ Currency from backend — replaces every hardcoded '£' below
  const { formatAmount, currency } = useCurrency();

  const getUserLabId = () => {
    try {
      return localStorage.getItem('userLabId');
    } catch (error) {
      console.error('Error getting userLabId from localStorage:', error);
      return null;
    }
  };

  useEffect(() => {
    const fetchSourceLabs = async () => {
      const userLabId = getUserLabId();
      if (!userLabId) return;
      try {
        const response = await fetch(`${API_BASE_URL}/api/outsource-invoice/source-labs`, {
          headers: {
            userlabid: userLabId,
            ...getAuthHeaders()
          },
        });
        if (!response.ok) {
          setSourceLabs([]);
          return;
        }
        const result = await response.json();
        if (result.success && Array.isArray(result.data)) {
          setSourceLabs(result.data);
        } else {
          setSourceLabs([]);
        }
      } catch (err) {
        console.error('Error fetching source labs:', err);
        setSourceLabs([]);
      }
    };
    fetchSourceLabs();
  }, []);

  const fetchEntries = async () => {
    try {
      setLoading(true);
      setError(null);

      const userLabId = getUserLabId();
      if (!userLabId) throw new Error('UserLabId not found in localStorage');

      const response = await fetch(`${API_BASE_URL}/api/outsource-invoice/posting-entries`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'userlabid': userLabId,
          ...getAuthHeaders()
        }
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const result = await response.json();
      if (result.success) {
        setEntries(result.data);
        applyClientFilters(result.data, filters);
      } else {
        throw new Error(result.message || 'Failed to fetch entries');
      }
    } catch (err) {
      console.error('Error fetching entries:', err);
      setError(err.message);
      setEntries([]);
      setFilteredEntries([]);
    } finally {
      setLoading(false);
    }
  };

  const applyClientFilters = (data, appliedFilters) => {
    let result = [...data];

    if (appliedFilters.startDate) {
      const start = new Date(appliedFilters.startDate);
      start.setHours(0, 0, 0, 0);
      result = result.filter((entry) => {
        if (!entry.postDate) return false;
        const d = new Date(entry.postDate);
        return d >= start;
      });
    }

    if (appliedFilters.endDate) {
      const end = new Date(appliedFilters.endDate);
      end.setHours(23, 59, 59, 999);
      result = result.filter((entry) => {
        if (!entry.postDate) return false;
        const d = new Date(entry.postDate);
        return d <= end;
      });
    }

    if (appliedFilters.type && appliedFilters.type !== 'All') {
      result = result.filter(
        (entry) =>
          entry.type &&
          entry.type.toLowerCase() === appliedFilters.type.toLowerCase()
      );
    }

    if (appliedFilters.accounts && !appliedFilters.accounts.includes('All')) {
      result = result.filter((entry) =>
        appliedFilters.accounts.includes(entry.account)
      );
    } else if (
      appliedFilters.account &&
      appliedFilters.account !== 'All' &&
      (!appliedFilters.accounts || appliedFilters.accounts.length === 0)
    ) {
      const accountList = appliedFilters.account.split(',').map((a) => a.trim());
      result = result.filter((entry) => accountList.includes(entry.account));
    }

    setFilteredEntries(result);
  };

  useEffect(() => {
    fetchEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreateNew = () => setShowDropdown(!showDropdown);

  const handleSelectOption = (option) => {
    if (option === 'credit') {
      setShowCreditModal(true);
      setShowDropdown(false);
    } else {
      setSelectedType(option.charAt(0).toUpperCase() + option.slice(1));
      setIsModalOpen(true);
      setShowDropdown(false);
    }
  };

  const handleCloseCreditModal = () => setShowCreditModal(false);
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedType('');
  };

  const handleModalCloseAndRefresh = () => {
    handleCloseModal();
    fetchEntries();
  };

  const handleCreditModalCloseAndRefresh = () => {
    handleCloseCreditModal();
    fetchEntries();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  // ⚠️ REMOVED: local formatAmount with hardcoded '£'
  // We now use formatAmount from useCurrency() above.

  const handleInvoiceClick = (invoiceNumber) => {
    if (invoiceNumber) {
      navigate(`/outsource-invoice/${invoiceNumber}`);
    }
  };

  const renderReference = (entry) => {
    const ref = entry.reference;
    if (!ref) return <span style={{ color: '#333' }}>N/A</span>;

    const refs = ref.split(',').map(r => r.trim()).filter(r => r.length > 0);

    if (refs.length === 1) {
      const singleRef = refs[0];
      if (singleRef.match(/^OS\d+/) || entry.sourceType === 'invoice' || entry.sourceType === 'credit' || entry.sourceType === 'payment' || entry.sourceType === 'credit_payment') {
        return (
          <span
            onClick={() => handleInvoiceClick(singleRef)}
            style={{
              color: '#007bff',
              cursor: 'pointer',
              textDecoration: 'none',
              marginRight: '5px'
            }}
          >
            {singleRef}
          </span>
        );
      }
      return <span style={{ color: '#333' }}>{singleRef}</span>;
    }

    return (
      <>
        {refs.map((r, index) => (
          <React.Fragment key={index}>
            <span
              onClick={() => handleInvoiceClick(r)}
              style={{
                color: '#007bff',
                cursor: 'pointer',
                textDecoration: 'none',
                marginRight: '5px'
              }}
            >
              {r}
            </span>
            {index < refs.length - 1 && <span style={{ color: '#333' }}>, </span>}
          </React.Fragment>
        ))}
      </>
    );
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const cellStyle = {
    textAlign: "left",
    borderBottom: "1px solid #ddd",
    color: "#333",
  };

  const handleApplyFilterWithFormat = (newFilters) => {
    setFilters(newFilters);
    setIsFilterModalOpen(false);
    applyClientFilters(entries, newFilters);
  };

  const getLabInfo = () => {
    let userData = null;
    try {
      const userDataStr =
        localStorage.getItem('userData') || sessionStorage.getItem('userData');
      if (userDataStr) userData = JSON.parse(userDataStr);
    } catch (e) {
      console.error('Error parsing userData:', e);
    }

    return {
      labName:
        userData?.labName ||
        userData?.name ||
        localStorage.getItem('labName') ||
        'Your Laboratory',
      address:
        userData?.address ||
        userData?.labAddress ||
        localStorage.getItem('labAddress') ||
        '',
      phone:
        userData?.phone ||
        userData?.labPhone ||
        localStorage.getItem('labPhone') ||
        '',
      email:
        userData?.email ||
        userData?.labEmail ||
        localStorage.getItem('labEmail') ||
        '',
    };
  };

  return (
    <div className="outsource-page">
      <Header />
      <Navvbar />
      <br />

      <div
        className="outsource-main-wrap"
        style={{
          width: "95%",
          margin: "0 auto",
          padding: "0px",
          minHeight: "calc(100vh - 200px)",
        }}
      >
        <div className="row outsource-main-row">
          {/* ================= SIDEBAR (same as Posting) ================= */}
          <FinanceSidebar variant="posting" />

          {/* ================= CONTENT ================= */}
          <div className="col-md-10 outsource-content">
            <h2
              className="outsource-title"
              style={{
                fontWeight: 500,
                color: "#333",
                marginTop: "20px",
                fontSize: "1.5rem",
                lineHeight: 1.2,
              }}
            >
              Outsource Posting Entries
            </h2>

            <div
              className="outsource-filter-area"
              style={{
                backgroundColor: "#f4f4f4",
                padding: "20px",
                marginTop: "20px",
                borderTop: "2px solid #004aad",
              }}
            >
              <div
                className="outsource-toolbar"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px",
                  position: "relative",
                }}
              >
                <div></div>

                <div
                  className="outsource-btn-row"
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <button
                    className="outsource-filter-btn"
                    onClick={() => setIsFilterModalOpen(true)}
                    style={{
                      padding: "8px 16px",
                      backgroundColor: "#004aad",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      marginRight: "10px",
                    }}
                  >
                    <FaRedo />
                    Filter
                  </button>

                  <div
                    className="outsource-create-wrap"
                    style={{ position: "relative" }}
                    ref={dropdownRef}
                  >
                    <button
                      className="outsource-create-btn"
                      onClick={handleCreateNew}
                      style={{
                        padding: "8px 16px",
                        backgroundColor: "#004aad",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      Create New
                      <IoCaretDown />
                    </button>

                    {showDropdown && (
                      <div
                        className="outsource-dropdown"
                        style={{
                          position: "absolute",
                          top: "100%",
                          right: 0,
                          marginTop: "5px",
                          backgroundColor: "white",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                          zIndex: 1000,
                          minWidth: "150px",
                        }}
                      >
                        <div className="outsource-dropdown-item" onClick={() => handleSelectOption('payment')} style={{ padding: "10px 15px", cursor: "pointer", borderBottom: "1px solid #eee" }}>Payment</div>
                        <div className="outsource-dropdown-item" onClick={() => handleSelectOption('invoice')} style={{ padding: "10px 15px", cursor: "pointer", borderBottom: "1px solid #eee" }}>Invoice</div>
                        <div className="outsource-dropdown-item" onClick={() => handleSelectOption('credit')} style={{ padding: "10px 15px", cursor: "pointer" }}>Credit</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div
                className="outsource-count-top"
                style={{
                  marginBottom: "15px",
                  fontSize: "16px",
                  fontWeight: "500",
                  color: "#333",
                }}
              >
                Showing 1 to {filteredEntries.length} of {filteredEntries.length} entries
                {loading && ' (Loading...)'}
              </div>
            </div>

            <div className="posting-table-wrapper">
              <Table
                hover
                id="outsource-postings"
                className="posting-table outsource-posting-table"
                style={{
                  borderCollapse: "collapse",
                  width: "100%",
                }}
              >
                <thead>
                  <tr style={{ fontSize: "18px" }}>
                    {[
                      "Post Date",
                      "Type",
                      "Lab",
                      "Amount",
                      "Reference",
                      "Statement Date",
                      "Action",
                    ].map((header, index) => (
                      <th
                        key={index}
                        style={{
                          backgroundColor: "#f4f4f4",
                          border: "none",
                          padding: "12px 16px",
                          textAlign: "left",
                          color: "#333",
                          fontWeight: "600",
                        }}
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody style={{ backgroundColor: "#fff" }}>
                  {loading ? (
                    <tr>
                      <td colSpan="7" style={{ textAlign: "center", padding: "40px" }}>
                        Loading entries...
                      </td>
                    </tr>
                  ) : error ? (
                    <tr>
                      <td colSpan="7" style={{ textAlign: "center", padding: "40px" }}>
                        <div style={{ color: "red" }}>Error loading entries</div>
                        <button
                          onClick={() => fetchEntries()}
                          style={{
                            marginTop: "10px",
                            padding: "8px 16px",
                            backgroundColor: "#004aad",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                          }}
                        >
                          Retry
                        </button>
                      </td>
                    </tr>
                  ) : filteredEntries.length > 0 ? (
                    filteredEntries.map((entry, index) => (
                      <tr
                        key={entry._id || index}
                        onMouseEnter={() => setHoveredRowId(entry._id || index)}
                        onMouseLeave={() => setHoveredRowId(null)}
                        style={{
                          backgroundColor: hoveredRowId === (entry._id || index) ? "#f9f9f9" : "#fff",
                          cursor: "pointer",
                        }}
                      >
                        <td style={cellStyle}>{formatDate(entry.postDate)}</td>
                        <td style={cellStyle}>{entry.type}</td>
                        <td style={cellStyle}>{entry.account}</td>
                        {/* ✅ Uses backend-driven currency symbol */}
                        <td style={cellStyle}>{formatAmount(entry.amount)}</td>
                        <td style={cellStyle}>{renderReference(entry)}</td>
                        <td style={cellStyle}>{formatDate(entry.statementDate)}</td>
                        <td style={cellStyle}></td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" style={{ textAlign: "center", padding: "40px" }}>
                        No posting entries found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            <div
              className="posting-bottom-count"
              style={{
                backgroundColor: "#f4f4f4",
                padding: "20px",
                marginTop: "-20px",
                borderTop: "2px solid #004aad",
                color: "#333",
              }}
            >
              Showing 1 to {filteredEntries.length} of {filteredEntries.length} entries
            </div>
          </div>
        </div>
      </div>

      <OutsourcePayment
        isOpen={isModalOpen}
        onClose={handleModalCloseAndRefresh}
        type={selectedType}
      />
      <OutsourceCredit
        isOpen={showCreditModal}
        onClose={handleCreditModalCloseAndRefresh}
      />

      <OutsourceFilterModel
        show={isFilterModalOpen}
        handleClose={() => setIsFilterModalOpen(false)}
        onFilterApply={handleApplyFilterWithFormat}
        accountOptions={sourceLabs.map((lab) => lab.labName)}
        exportEntries={entries}
        labInfo={getLabInfo()}
      />

      {/* =======================================================
          RESPONSIVE CSS — matches Posting page behavior
      ======================================================== */}
      <style>{`
        /* Title — non-bold, same feel as Posting page */
        .outsource-page .outsource-title {
          font-weight: 500 !important;
          color: #333;
        }

        /* =====================================================
           SMALL SCREEN  (DESKTOP UNCHANGED)
        ====================================================== */
        @media (max-width: 767px) {

          .outsource-page {
            width: 96% !important;
            max-width: 96% !important;
            margin-left: auto !important;
            margin-right: auto !important;
            padding: 0 !important;
            overflow: visible !important;
            min-height: auto !important;
          }

          .outsource-page .outsource-main-wrap {
            width: 96% !important;
            max-width: 96% !important;
            margin-left: auto !important;
            margin-right: auto !important;
            min-height: auto !important;
          }

          .outsource-page .outsource-main-row {
            display: flex !important;
            flex-direction: row !important;
            flex-wrap: nowrap !important;
            width: 100% !important;
            max-width: 100% !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
            align-items: flex-start !important;
          }

          .outsource-page .outsource-content {
            flex: 0 0 85% !important;
            width: 85% !important;
            max-width: 85% !important;
            min-width: 0 !important;
            padding-left: 3px !important;
            padding-right: 0 !important;
            margin: 0 !important;
            overflow: visible !important;
          }

          /* Mobile title — small, non-bold */
          .outsource-page .outsource-title {
            font-size: 10px !important;
            font-weight: 500 !important;
            line-height: 1 !important;
            margin-top: 4px !important;
            margin-bottom: 5px !important;
            padding: 0 !important;
            white-space: nowrap !important;
          }

          .outsource-page .outsource-filter-area {
            width: 100% !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
            padding: 4px !important;
            margin-top: 4px !important;
            border-top: 1px solid #004aad !important;
            overflow: visible !important;
          }

          .outsource-page .outsource-toolbar {
            display: flex !important;
            flex-direction: row !important;
            flex-wrap: nowrap !important;
            width: 100% !important;
            justify-content: flex-end !important;
            align-items: center !important;
            margin-bottom: 4px !important;
            min-height: 17px !important;
          }

          .outsource-page .outsource-btn-row {
            display: flex !important;
            flex-direction: row !important;
            flex-wrap: nowrap !important;
            align-items: center !important;
            justify-content: flex-end !important;
            gap: 0 !important;
            height: 16px !important;
            margin: 0 !important;
            padding: 0 !important;
          }

          .outsource-page .outsource-create-wrap {
            display: inline-flex !important;
            align-items: center !important;
            flex: 0 0 auto !important;
            width: auto !important;
            height: 16px !important;
            margin: 0 !important;
            padding: 0 !important;
          }

          .outsource-page .outsource-filter-btn,
          .outsource-page .outsource-create-btn {
            display: inline-flex !important;
            flex-direction: row !important;
            flex-wrap: nowrap !important;
            align-items: center !important;
            justify-content: center !important;
            flex: 0 0 auto !important;
            flex-shrink: 0 !important;
            box-sizing: border-box !important;
            font-size: 5px !important;
            line-height: 1 !important;
            padding: 2px 3px !important;
            gap: 2px !important;
            width: auto !important;
            height: 16px !important;
            min-height: 16px !important;
            max-height: 16px !important;
            margin: 0 1px 0 0 !important;
            border-radius: 2px !important;
            white-space: nowrap !important;
            vertical-align: middle !important;
            position: static !important;
            top: auto !important;
          }

          .outsource-page .outsource-filter-btn svg,
          .outsource-page .outsource-create-btn svg {
            display: block !important;
            width: 6px !important;
            height: 6px !important;
            margin: 0 !important;
            flex-shrink: 0 !important;
            vertical-align: middle !important;
          }

          .outsource-page .outsource-dropdown {
            min-width: 60px !important;
            margin-top: 2px !important;
            font-size: 6px !important;
          }

          .outsource-page .outsource-dropdown-item {
            font-size: 6px !important;
            padding: 3px 5px !important;
            line-height: 1.1 !important;
            white-space: nowrap !important;
          }

          .outsource-page .outsource-count-top {
            font-size: 5px !important;
            line-height: 1 !important;
            margin-bottom: 3px !important;
            white-space: nowrap !important;
          }

          /* ---------------- TABLE ---------------- */
          .outsource-page .posting-table-wrapper {
            display: block !important;
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: visible !important;
          }

          .outsource-page .outsource-posting-table {
            display: table !important;
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
            margin: 0 !important;
            table-layout: fixed !important;
            border-collapse: collapse !important;
            font-size: 5px !important;
            box-sizing: border-box !important;
          }

          .outsource-page .outsource-posting-table thead tr {
            font-size: 5px !important;
          }

          .outsource-page .outsource-posting-table thead th,
          .outsource-page .outsource-posting-table tbody td {
            font-size: 5px !important;
            padding: 2px 1px !important;
            line-height: 1.05 !important;
            vertical-align: middle !important;
            text-align: left !important;
            white-space: normal !important;
            word-break: break-all !important;
            overflow-wrap: anywhere !important;
            overflow: hidden !important;
            height: auto !important;
            box-sizing: border-box !important;
          }

          .outsource-page .outsource-posting-table thead th {
            font-weight: 600 !important;
          }

          .outsource-page .outsource-posting-table th:nth-child(1),
          .outsource-page .outsource-posting-table td:nth-child(1) { width: 13% !important; }
          .outsource-page .outsource-posting-table th:nth-child(2),
          .outsource-page .outsource-posting-table td:nth-child(2) { width: 10% !important; }
          .outsource-page .outsource-posting-table th:nth-child(3),
          .outsource-page .outsource-posting-table td:nth-child(3) { width: 20% !important; }
          .outsource-page .outsource-posting-table th:nth-child(4),
          .outsource-page .outsource-posting-table td:nth-child(4) { width: 12% !important; }
          .outsource-page .outsource-posting-table th:nth-child(5),
          .outsource-page .outsource-posting-table td:nth-child(5) { width: 17% !important; }
          .outsource-page .outsource-posting-table th:nth-child(6),
          .outsource-page .outsource-posting-table td:nth-child(6) { width: 16% !important; }
          .outsource-page .outsource-posting-table th:nth-child(7),
          .outsource-page .outsource-posting-table td:nth-child(7) { width: 12% !important; }

          .outsource-page .outsource-posting-table span,
          .outsource-page .outsource-posting-table a {
            font-size: 5px !important;
            line-height: 1.05 !important;
            margin-right: 1px !important;
            white-space: normal !important;
            word-break: break-all !important;
            overflow-wrap: anywhere !important;
          }

          .outsource-page .outsource-posting-table td[colspan="7"] {
            width: 100% !important;
            max-width: 100% !important;
            font-size: 6px !important;
            padding: 10px 2px !important;
            text-align: center !important;
          }

          .outsource-page .outsource-posting-table td[colspan="7"] button {
            font-size: 5px !important;
            padding: 1px 4px !important;
            margin-top: 3px !important;
          }

          .outsource-page .posting-bottom-count {
            width: 100% !important;
            font-size: 5px !important;
            line-height: 1 !important;
            padding: 4px !important;
            margin-top: 0 !important;
            border-top: 1px solid #004aad !important;
            box-sizing: border-box !important;
            white-space: nowrap !important;
          }
        }

        /* =====================================================
           VERY SMALL SCREEN
        ====================================================== */
        @media (max-width: 480px) {
          .outsource-page {
            width: 98% !important;
            max-width: 98% !important;
          }

          .outsource-page .outsource-content {
            flex: 0 0 86% !important;
            width: 86% !important;
            max-width: 86% !important;
            padding-left: 2px !important;
            padding-right: 0 !important;
          }

          .outsource-page .outsource-title {
            font-size: 9px !important;
            font-weight: 500 !important;
          }

          .outsource-page .outsource-btn-row,
          .outsource-page .outsource-create-wrap { height: 14px !important; }

          .outsource-page .outsource-filter-btn,
          .outsource-page .outsource-create-btn {
            font-size: 4px !important;
            height: 14px !important;
            min-height: 14px !important;
            max-height: 14px !important;
            padding: 1px 2px !important;
          }

          .outsource-page .outsource-filter-btn svg,
          .outsource-page .outsource-create-btn svg {
            width: 5px !important;
            height: 5px !important;
          }

          .outsource-page .outsource-dropdown-item { font-size: 5px !important; }

          .outsource-page .outsource-count-top,
          .outsource-page .posting-bottom-count { font-size: 4px !important; }

          .outsource-page .outsource-posting-table,
          .outsource-page .outsource-posting-table thead tr,
          .outsource-page .outsource-posting-table thead th,
          .outsource-page .outsource-posting-table tbody td,
          .outsource-page .outsource-posting-table span,
          .outsource-page .outsource-posting-table a {
            font-size: 4px !important;
            line-height: 1 !important;
          }
        }

        /* =====================================================
           360px AND BELOW
        ====================================================== */
        @media (max-width: 360px) {
          .outsource-page {
            width: 99% !important;
            max-width: 99% !important;
          }

          .outsource-page .outsource-content {
            flex: 0 0 87% !important;
            width: 87% !important;
            max-width: 87% !important;
            padding-left: 1px !important;
          }

          .outsource-page .outsource-title {
            font-size: 8px !important;
            font-weight: 500 !important;
          }

          .outsource-page .outsource-btn-row,
          .outsource-page .outsource-create-wrap { height: 13px !important; }

          .outsource-page .outsource-filter-btn,
          .outsource-page .outsource-create-btn {
            font-size: 3.5px !important;
            height: 13px !important;
            min-height: 13px !important;
            max-height: 13px !important;
            padding: 1px 2px !important;
          }

          .outsource-page .outsource-posting-table,
          .outsource-page .outsource-posting-table thead th,
          .outsource-page .outsource-posting-table tbody td,
          .outsource-page .outsource-posting-table span,
          .outsource-page .outsource-posting-table a,
          .outsource-page .outsource-count-top,
          .outsource-page .posting-bottom-count {
            font-size: 3.5px !important;
          }
        }
      `}</style>
    </div>
  )
}

export default OutsourcePosting